// The one optimistic-write runner (T23 §1). The apply → write → ack/rollback flow
// used to be duplicated in the items/categories/todos stores; it lives here now so
// the write timeout has a single home: a repo write that *hangs* (never resolves)
// no longer leaves its delta pending forever — it is rolled back after `timeoutMs`
// and surfaced as a Network error, once. A write that settles late is ignored (the
// delta is already gone; if it did land server-side, the authoritative stream
// converges local state anyway).
import { networkError } from "@pack-it-up/shared";
import { applyOptimistic, ackWrite, rollback, get as rget, } from "./reconcile.js";
import { errorToMessage } from "../errorToMessage.js";
// How long a write may stay un-acked before it is rolled back. Overridable per
// store (deps) so tests can use a short timeout.
export const OPTIMISTIC_WRITE_TIMEOUT_MS = 10_000;
// Race the write against the timeout. The loser's resolution is a no-op (a promise
// resolves once), so a late write result can't re-surface an error or re-ack.
const raceTimeout = (write, ms) => new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, error: networkError("write timed out") }), ms);
    void write.then((r) => {
        clearTimeout(timer);
        resolve(r);
    });
});
export const createOptimisticRunner = (deps) => {
    const timeoutMs = deps.timeoutMs ?? OPTIMISTIC_WRITE_TIMEOUT_MS;
    let seq = 0;
    // Fire the write and settle the delta: ack on Ok; roll back + surface the error on
    // Err or timeout. Slices are re-read from the store on settle: the optimistic set()
    // replaced the one captured before the await, and a snapshot may have arrived too.
    const settle = async (deltaId, write) => {
        const r = await raceTimeout(write(), timeoutMs);
        if (r.ok)
            deps.set({ slice: ackWrite(deps.getSlice(), deltaId) });
        else
            deps.set({ slice: rollback(deps.getSlice(), deltaId), error: errorToMessage(r.error) });
        return r;
    };
    return {
        async mutate(id, mutate, write) {
            const slice = deps.getSlice();
            const cur = rget(slice, id);
            if (!cur)
                return { ok: false, error: { kind: "NotFound", entity: deps.entity, id } };
            const deltaId = `${deps.deltaPrefix}-${seq++}`;
            deps.set({ slice: applyOptimistic(slice, { deltaId, id, before: cur, after: mutate(cur) }), error: null });
            return settle(deltaId, write);
        },
        async remove(id, write) {
            const slice = deps.getSlice();
            const cur = rget(slice, id);
            if (!cur) {
                // No local row to optimistically remove or roll back — just issue the delete
                // (still raced against the timeout so a hang surfaces an error).
                const r = await raceTimeout(write(), timeoutMs);
                if (!r.ok)
                    deps.set({ slice: deps.getSlice(), error: errorToMessage(r.error) });
                return r;
            }
            const deltaId = `${deps.deltaPrefix}-${seq++}`;
            // Optimistic delete: `before` and no `after` removes the row now; rollback restores it.
            deps.set({ slice: applyOptimistic(slice, { deltaId, id, before: cur }), error: null });
            return settle(deltaId, write);
        },
    };
};
//# sourceMappingURL=optimistic.js.map